import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify"; 
// Make sure updateClientStatusAPI in apiHelper accepts a STRING now, not boolean
import { fetchClientDetailAPI, updateClientStatusAPI } from "api/apiHelper"; 

// Components
import Card from "components/Card";
import Backdrop from "components/Backdrop";
import ClientUserAssignment from "./ClientUserAssignment";
import ClientEmployeeAssignment from "./ClientEmployeeAssignent";

interface ClientData {
  id: number;
  name: string;
  email: string | null;
  city: string | null;
  country: string | null;
  logo: string | null;
  // ✅ Updated fields
  status: 'ACTIVE' | 'INACTIVE' | 'ON_HOLD'; 
  start_date: string;
  end_date: string | null;
}

const ManageClient = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  
  const [client, setClient] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);

  // --- Fetch Client Details ---
  useEffect(() => {
    const fetchClientData = async () => {
      if (!clientId) return;
      try {
        setLoading(true);
        const response = await fetchClientDetailAPI(clientId);
        setClient(response.data);
      } catch (error) {
        console.error("Failed to fetch client details", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  // --- Handle Status Change ---
  const handleStatusChange = async (newStatus: string) => {
    if (!client || !clientId) return;

    try {
        setUpdating(true);
        
        await updateClientStatusAPI(clientId, newStatus as any);
        
        const today = new Date().toISOString().split('T')[0];
        
        setClient({ 
            ...client, 
            status: newStatus as any,
            end_date: newStatus === 'INACTIVE' ? today : null 
        });
        
        toast.success(`Client status updated to ${newStatus}`);
    } catch (error) {
        console.error("Failed to update status", error);
        toast.error("Failed to update status");
    } finally {
        setUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
      if(status === 'ACTIVE') return 'bg-green-100 text-green-700 border-green-200';
      if(status === 'ON_HOLD') return 'bg-orange-100 text-orange-700 border-orange-200';
      return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  if (loading) return <Backdrop show={true} />;
  if (!client) return <div className="p-5 text-red-500">Client not found</div>;

  return (
    <div className="mt-5 space-y-6">
      
      <Card extra="w-full p-6 bg-white dark:bg-navy-800 rounded-xl shadow-sm border-l-4 border-brand-500">
        <div className="flex flex-col md:flex-row justify-between items-start gap-6">
            
            <div className="flex items-center gap-5">
                <div className="h-24 w-24 rounded-xl bg-gray-50 dark:bg-navy-700 flex items-center justify-center text-4xl text-gray-300 border border-gray-100 dark:border-navy-600 overflow-hidden shadow-sm">
                    {client.logo ? (
                        <img src={client.logo} alt="logo" className="h-full w-full object-cover"/>
                    ) : (
                        <i className="fas fa-building"></i>
                    )}
                </div>

                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                            {client.name}
                        </h1>
                        <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold border ${getStatusColor(client.status)}`}>
                            {client.status.replace("_", " ")}
                        </span>
                    </div>

                    <div className="flex flex-col gap-1.5 mt-2">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
                            <i className="fas fa-map-marker-alt w-5 text-center text-brand-500"></i>
                            <span>
                                {client.city || "No City"}, {client.country || "No Country"}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
                            <i className="fas fa-envelope w-5 text-center text-brand-500"></i>
                            <a href={`mailto:${client.email}`} className="hover:text-brand-600">
                                {client.email || "No Email"}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="flex flex-col items-end gap-4 w-full md:w-auto">
                
                <div className="flex items-center gap-3 bg-gray-50 dark:bg-navy-700 p-2 rounded-lg border border-gray-200 dark:border-navy-600">
                    <span className="text-xs font-semibold uppercase text-gray-500 pl-1">Status:</span>
                    <select
                        value={client.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={updating}
                        className="bg-white dark:bg-navy-800 border border-gray-300 dark:border-navy-600 text-gray-900 dark:text-white text-sm rounded-md focus:ring-brand-500 focus:border-brand-500 block p-1.5"
                    >
                        <option value="ACTIVE">Active</option>
                        <option value="ON_HOLD">On Hold</option>
                        <option value="INACTIVE">Inactive</option>
                    </select>
                </div>
                <div className="text-right space-y-1">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Started:</span> {client.start_date || "N/A"}
                    </div>
                    {client.end_date && (
                         <div className="text-sm text-red-500 font-medium">
                            Ended: {client.end_date}
                        </div>
                    )}
                </div>
                
                <button 
                    onClick={() => navigate(-1)}
                    className="text-sm text-gray-500 hover:text-brand-500 hover:underline flex items-center gap-1 transition-colors mt-2"
                >
                    <i className="fas fa-arrow-left"></i> Back to List
                </button>
            </div>
        </div>
      </Card>

      <ClientUserAssignment clientId={clientId} />

      <div className="flex items-center gap-4 py-2">
         <div className="h-px bg-gray-300 dark:bg-navy-700 flex-1"></div>
         <span className="text-gray-400 text-sm font-medium uppercase tracking-wider">Assigned Employees</span>
         <div className="h-px bg-gray-300 dark:bg-navy-700 flex-1"></div>
      </div>

      <ClientEmployeeAssignment clientId={clientId} />
      
    </div>
  );
};

export default ManageClient;